import { useEffect, useState } from "react"

import { env } from "@/config/env"
import { useAuthStore } from "@/stores/auth-store"
import {
  subscriptionsService,
  SubscriptionPolicy,
  type UserSubscription,
} from "@/features/subscriptions"
import { DEV_BYPASS_AUTH, DEV_BYPASS_SUBSCRIPTION } from "@/config/dev-bypass"

const policy = new SubscriptionPolicy({ bundle: env.VITE_SUBSCRIPTION_BUNDLE })

export interface ActivePlanState {
  isLoading: boolean
  subscription: UserSubscription | null
}

/**
 * Loads the signed-in user's active plan from the subscriptions backend
 * (`SubscriptionsService.getUserSubscriptions`) when the My Plan screen opens,
 * exposing `isLoading` for the "Fetching plans" state.
 *
 * With no real session (e.g. local dev) the backend returns nothing; under the
 * dev bypass we then fall back to a stub plan so the screen stays testable. In
 * production the signed-in user's real plan is returned.
 */
export function useActivePlan(): ActivePlanState {
  const uid = useAuthStore((s) => s.user?.uid ?? null)
  const appleToken = useAuthStore((s) => s.user?.appleToken ?? null)
  const [state, setState] = useState<ActivePlanState>({ isLoading: true, subscription: null })

  useEffect(() => {
    let cancelled = false

    const load = async (): Promise<UserSubscription | null> => {
      // Apple sign-in keys subscriptions by the Apple token; otherwise by uid.
      const key = appleToken ?? uid ?? ""
      let subscriptions: UserSubscription[]
      try {
        subscriptions = await subscriptionsService.getUserSubscriptions(key)
      } catch {
        subscriptions = []
      }
      const active = policy.findAccessSubscription(subscriptions)
      if (active) return active
      // No real session (local dev): keep the screen testable under the bypass.
      return DEV_BYPASS_AUTH ? DEV_BYPASS_SUBSCRIPTION : null
    }

    void load().then((subscription) => {
      if (!cancelled) setState({ isLoading: false, subscription })
    })

    return () => {
      cancelled = true
    }
  }, [uid, appleToken])

  return state
}
